import { useFetch } from "~vue/api";
import type { ConversationSearchRequest, ConversationSearchResponse, SearchResultItem, SearchResultSession } from "~vue/api/generated/conversation-search";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, type MaybeRefOrGetter, ref, toValue, watch } from "vue";
export type {
    ConversationSearchResponse,
    SearchMetadata,
    SearchResultItem,
    SearchResultMessage,
    SearchResultSession,
    SearchResultType,
} from "~vue/api/generated/conversation-search";

export type SearchOptions = Pick<ConversationSearchRequest, "scope" | "roles" | "purposes" | "content_filter" | "sources" | "include_artifacts" | "include_legacy_documents">;

export interface GroupedSearchResult {
    session: SearchResultSession;
    results: SearchResultItem[];
    topRelevance: number;
    matchCount: number;
    latestDate: string;
    topResult: SearchResultItem;
}

// Per-tab cache of the last completed search so back-navigation restores state
// without firing another LLM call. Cache is intentionally session-scoped — closing
// the tab clears it.
const STORAGE_KEY = "conversation-search-state";

const CACHE_TTL_MS = 5 * 60 * 1000;
export type SearchFilterKey = "chat" | "uploaded_docs" | "created_docs";

interface PersistedState {
    ownerId: string;
    expiresAt: number;
    filters: SearchFilterKey[];
    query: string;
    options?: SearchOptions;
    response: ConversationSearchResponse | null;
    hasSearched: boolean;
    searchCount: number;
    lastSearchId: string;
}

export function useConversationSearch(opts?: { maxAttempts?: number; ownerId?: MaybeRefOrGetter<string> }) {
    const maxAttempts = opts?.maxAttempts ?? 3;
    const ownerId = computed(() => toValue(opts?.ownerId) ?? "");
    const selectedFilters = ref<SearchFilterKey[]>(["chat"]);
    const query = ref("");
    const lastOptions = ref<SearchOptions>({});
    const searchPayload = ref<{ search_id: string; query: string } | null>(null);
    const hasSearched = ref(false);
    const searchCount = ref(0);
    const lastSearchId = ref("");
    const responseSource = ref<ConversationSearchResponse | null>(null);

    const { execute } = useFetch("/v1/conversation-search/search", { immediate: false }).post(searchPayload).json<ConversationSearchResponse>();
    const isFetching = ref(false);
    const error = ref<unknown>(null);
    const searchGeneration = ref(0);

    const results = computed(() => responseSource.value?.results ?? []);
    const artifacts = computed(() => responseSource.value?.artifacts ?? []);
    const metadata = computed(() => responseSource.value?.metadata ?? null);

    const groupedResults = computed((): GroupedSearchResult[] => {
        const groups = new Map<string, GroupedSearchResult>();
        for (const item of results.value) {
            const key = item.session.uuid;
            const existing = groups.get(key);
            if (existing) {
                existing.results.push(item);
                existing.matchCount++;
                if (item.message.when > existing.latestDate) {
                    existing.latestDate = item.message.when;
                }
                if (item.relevance > existing.topRelevance) {
                    existing.topRelevance = item.relevance;
                    existing.topResult = item;
                }
            } else {
                groups.set(key, {
                    session: item.session,
                    results: [item],
                    topRelevance: item.relevance,
                    matchCount: 1,
                    latestDate: item.message.when,
                    topResult: item,
                });
            }
        }
        return [...groups.values()].sort((a, b) => (a.latestDate > b.latestDate ? -1 : 1));
    });

    function buildSearchPayload(searchId: string, q: string, options?: SearchOptions): { search_id: string; query: string } {
        return { search_id: searchId, query: q, ...options };
    }

    async function search(q: string, options?: SearchOptions, filters: SearchFilterKey[] = ["chat"]) {
        const trimmed = q.trim();
        if (!trimmed) {
            return;
        }
        const searchId = globalThis.crypto.randomUUID();
        lastSearchId.value = searchId;
        query.value = trimmed;
        lastOptions.value = options ?? {};
        selectedFilters.value = [...filters];
        searchPayload.value = buildSearchPayload(searchId, trimmed, options);
        hasSearched.value = true;
        searchCount.value++;
        error.value = null;
        isFetching.value = true;
        searchGeneration.value++;
        const generation = searchGeneration.value;
        try {
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    const completed: Response | null = await execute(true);
                    if (searchGeneration.value !== generation) {
                        break;
                    }
                    if (!completed?.ok) {
                        // beforeFetch cancellation retains shared data but has no response.
                        error.value = new Error("Search request did not complete");
                        break;
                    }
                    const payload: ConversationSearchResponse = await completed.json();
                    if (searchGeneration.value !== generation) {
                        break;
                    }
                    if (!Array.isArray(payload?.results) || !payload.metadata || (payload.artifacts !== undefined && !Array.isArray(payload.artifacts))) {
                        throw new Error("Invalid search response");
                    }
                    responseSource.value = payload;
                    persist();
                    break;
                } catch (requestError) {
                    if (searchGeneration.value !== generation) {
                        break;
                    }
                    if (attempt < maxAttempts) {
                        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
                    } else {
                        error.value = requestError;
                    }
                }
            }
        } finally {
            if (searchGeneration.value === generation) {
                isFetching.value = false;
            }
        }

        if (searchGeneration.value === generation) {
            void logUserInteraction(AmplitudeEventNames.CONVERSATION_SEARCH_SUBMITTED, {
                search_id: searchId,
                result_count: results.value.length,
                is_refinement: searchCount.value > 1,
                scope: options?.scope ?? "chat",
            });
        }
    }

    async function refresh() {
        if (!query.value) {
            return;
        }
        await search(query.value, lastOptions.value, selectedFilters.value);
    }

    function logResultClicked(item: SearchResultItem, position: number) {
        void logUserInteraction(AmplitudeEventNames.CONVERSATION_SEARCH_RESULT_CLICKED, {
            search_id: lastSearchId.value,
            session_uuid: item.session.uuid,
            message_id: item.message.id,
            relevance: item.relevance,
            result_position: position,
        });
    }

    function reset() {
        query.value = "";
        lastOptions.value = {};
        selectedFilters.value = ["chat"];
        searchPayload.value = null;
        hasSearched.value = false;
        searchCount.value = 0;
        lastSearchId.value = "";
        responseSource.value = null;
        error.value = null;
        isFetching.value = false;
        searchGeneration.value++;
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {
            // Storage unavailable (e.g. private mode quota); ignore.
        }
    }

    function persist() {
        if (!ownerId.value) {
            return;
        }
        try {
            const state: PersistedState = {
                ownerId: ownerId.value,
                expiresAt: Date.now() + CACHE_TTL_MS,
                filters: [...selectedFilters.value],
                query: query.value,
                options: lastOptions.value,
                response: responseSource.value,
                hasSearched: hasSearched.value,
                searchCount: searchCount.value,
                lastSearchId: lastSearchId.value,
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
            // sessionStorage is unavailable (private-browsing quota or disabled);
            // back-nav restore won't work but the live in-memory state still does.
        }
    }

    function hydrate(): boolean {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return false;
            }
            const state = JSON.parse(raw) as PersistedState;
            if (!ownerId.value || state.ownerId !== ownerId.value || !(state.expiresAt > Date.now()) || !Array.isArray(state.filters) || !state.hasSearched || !state.response) {
                sessionStorage.removeItem(STORAGE_KEY);
                return false;
            }
            selectedFilters.value = state.filters;
            query.value = state.query;
            lastOptions.value = state.options ?? {};
            responseSource.value = state.response;
            hasSearched.value = state.hasSearched;
            searchCount.value = state.searchCount;
            lastSearchId.value = state.lastSearchId;
            return true;
        } catch {
            // Stored payload is malformed (likely an older shape after a
            // composable refactor) — clear it so the next persist starts clean.
            try {
                sessionStorage.removeItem(STORAGE_KEY);
            } catch {
                // Storage itself is unavailable; nothing to clear.
            }
            return false;
        }
    }

    watch(ownerId, reset, { flush: "sync" });

    return {
        query,
        selectedFilters,
        lastOptions,
        artifacts,
        results,
        metadata,
        groupedResults,
        isFetching,
        error,
        hasSearched,
        searchCount,
        lastSearchId,
        search,
        refresh,
        logResultClicked,
        reset,
        hydrate,
    };
}
